import React from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../util/reactIntl';
import { ensureUser } from '../../util/data';
import { profileTabSearch, PROFILE_TAB_LISTINGS } from '../../util/profilePageTabs';

import { AvatarSmall, ListingCard, NamedLink } from '../../components';
import { getSearchResultsCardRenderSizes } from '../SearchPage/SearchResultsPanel/searchResultsPanelHelpers';

import { LISTING_PAGE_MORE_FROM_SELLER_LIMIT } from './ListingPage.duck';

import css from './SectionMoreFromSeller.module.css';

/**
 * Full-width “More from this seller” rail below the listing body.
 * Omitted when the seller has no other published listings.
 *
 * @component
 */
const SectionMoreFromSeller = props => {
  const {
    author,
    currentListingId,
    listings = [],
    authorPublishedListingCount = null,
    className,
  } = props;

  const user = ensureUser(author);
  const userId = user?.id?.uuid;
  const displayName = user?.attributes?.profile?.displayName || '';
  const currentId = currentListingId?.uuid || currentListingId;

  const otherListings = listings
    .filter(l => {
      const id = l?.id?.uuid;
      if (!id || id === currentId) {
        return false;
      }
      const { deleted, state } = l?.attributes || {};
      return !deleted && state === 'published';
    })
    .slice(0, LISTING_PAGE_MORE_FROM_SELLER_LIMIT);

  const otherCount =
    typeof authorPublishedListingCount === 'number'
      ? Math.max(0, authorPublishedListingCount - 1)
      : otherListings.length;

  if (!userId || otherListings.length === 0 || otherCount < 1) {
    return null;
  }

  const cardRenderSizes = getSearchResultsCardRenderSizes(false);

  return (
    <section className={classNames(css.root, className)} data-testid="more-from-seller">
      <div className={css.divider} />
      <div className={css.header}>
        <AvatarSmall className={css.avatar} user={user} />
        <div className={css.headerText}>
          <h2 className={css.title}>
            <FormattedMessage id="ListingPage.moreFromSellerTitle" values={{ name: displayName }} />
          </h2>
          <p className={css.subline}>
            <FormattedMessage
              id="ListingPage.moreFromSellerOtherCount"
              values={{ count: otherCount }}
            />
          </p>
        </div>
        <div className={css.headerSpacer} />
        <NamedLink
          className={css.viewLink}
          name="ProfilePage"
          params={{ id: userId }}
          to={{ search: profileTabSearch(PROFILE_TAB_LISTINGS) }}
        >
          <FormattedMessage
            id="ListingPage.moreFromSellerViewListings"
            values={{ name: displayName }}
          />
          <svg
            className={css.viewChevron}
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="m9 6 6 6-6 6" />
          </svg>
        </NamedLink>
      </div>
      <ul className={css.rail}>
        {otherListings.map(listing => (
          <li className={css.railItem} key={listing.id.uuid}>
            <ListingCard
              className={css.listingCard}
              listing={listing}
              showAuthorInfo={false}
              renderSizes={cardRenderSizes}
            />
          </li>
        ))}
      </ul>
    </section>
  );
};

export default SectionMoreFromSeller;
